import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router, Routes, Route, Navigate,
} from 'react-router-dom';
import { onAuthStateChanged, signInWithCustomToken, sendEmailVerification, signOut } from 'firebase/auth';
import { doc, getDoc, onSnapshot, setDoc, updateDoc, deleteField } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from './firebase';

import HomePage from './HomePage';
import CreateProfile from './CreateProfile';
import OnboardingPage from './OnboardingPage';
import PublicProfile from './PublicProfile';
import Settings from './Settings';
import InviteLanding, { INVITE_TOKEN_KEY } from './InviteLanding';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';
import DeleteAccount from './DeleteAccount';
import Legal from './Legal';
import Help from './Help';
import Feedback from './Feedback';
import AdminPanel from './AdminPanel';
import PeoplePage from './PeoplePage';
import { computeRail, getOrientationInfo, SHOW_RAIL_DEBUG } from './railSide';

// Temporary on-screen readout to calibrate the landscape left/right mapping.
function RailDebug() {
  const [info, setInfo] = useState(() => ({ ...getOrientationInfo(), side: computeRail() }));
  useEffect(() => {
    const update = () => setInfo({ ...getOrientationInfo(), side: computeRail() });
    const so = window.screen && window.screen.orientation;
    if (so && so.addEventListener) so.addEventListener('change', update);
    window.addEventListener('orientationchange', update);
    window.addEventListener('resize', update);
    return () => {
      if (so && so.removeEventListener) so.removeEventListener('change', update);
      window.removeEventListener('orientationchange', update);
      window.removeEventListener('resize', update);
    };
  }, []);
  return (
    <div style={{
      position: 'fixed', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 9999,
      background: 'rgba(0,0,0,0.85)', color: '#fff', padding: '6px 10px', borderRadius: 8,
      fontSize: 11, fontFamily: 'monospace', pointerEvents: 'none', whiteSpace: 'nowrap',
      border: '1px solid #555',
    }}>
      {info.type} · angle {String(info.angle)} · rail: {info.side}
    </div>
  );
}

function FullPageSpinner() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 18,
      background: 'var(--bg)',
    }}>
      <div style={{
        fontSize: 28, fontWeight: 900,
        background: 'linear-gradient(135deg, var(--accent), var(--accent-2))',
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        backgroundClip: 'text', letterSpacing: '-1px',
      }}>Your Top 8</div>
      <span className="spinner" style={{ width: 26, height: 26 }} />
    </div>
  );
}

function ProfileLoadError() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 14,
      background: 'var(--bg)', color: 'var(--text)', padding: 24, textAlign: 'center',
    }}>
      <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>Couldn't load your profile</h1>
      <p style={{ color: 'var(--muted)', margin: 0, fontSize: 14, maxWidth: 340, lineHeight: 1.6 }}>
        Check your connection and try again.
      </p>
      <button className="btn btn-primary" onClick={() => window.location.reload()} style={{ marginTop: 4 }}>
        Retry
      </button>
    </div>
  );
}

// Gate for unverified email/password accounts. Google (and the native bridge)
// come back already verified, so they never see this. Enforced here so an
// unverified session can't reach the app even if it somehow persists; the
// users-create rule also requires email_verified as a backstop.
function VerifyEmail({ user, onSignedOut }) {
  const [sent, setSent] = useState(false);
  const [stillUnverified, setStillUnverified] = useState(false);
  const resend = async () => { try { await sendEmailVerification(user); setSent(true); } catch { /* ignore */ } };
  const recheck = async () => {
    try { await user.reload(); } catch { /* ignore */ }
    if (auth.currentUser?.emailVerified) {
      // Refresh the ID token so its email_verified claim is current before we
      // reload; otherwise backend calls keep seeing the stale (unverified) token.
      try { await auth.currentUser.getIdToken(true); } catch { /* ignore */ }
      window.location.reload();
    } else setStillUnverified(true);
  };
  const leave = async () => { try { await signOut(auth); } catch { /* ignore */ } onSignedOut?.(); };
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, background: 'var(--bg)', color: 'var(--text)', padding: 24, textAlign: 'center' }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>Verify your email</h1>
      <p style={{ color: 'var(--muted)', margin: 0, fontSize: 14, maxWidth: 360, lineHeight: 1.6 }}>
        We sent a verification link to <strong style={{ color: 'var(--text)' }}>{user.email}</strong>. Click it, then come back and continue.
      </p>
      {stillUnverified && <p style={{ color: 'var(--bad, #ff6b6b)', fontSize: 13, margin: 0 }}>Still not verified. Check the link, then try again.</p>}
      {sent && <p style={{ color: 'var(--good, #7ddb7d)', fontSize: 13, margin: 0 }}>Sent. Check your inbox (and spam).</p>}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', marginTop: 4 }}>
        <button className="btn btn-primary" onClick={recheck}>I've verified</button>
        <button className="btn btn-secondary" onClick={resend}>Resend email</button>
        <button className="btn btn-ghost" onClick={leave}>Sign out</button>
      </div>
    </div>
  );
}

// New accounts see the intro carousel once; a per-device flag or the server-side
// introSeenAt suppresses it on every load after.
function introSeen(profile) {
  try { if (localStorage.getItem('yt8_intro_seen')) return true; } catch {}
  return !!profile?.introSeenAt;
}

function App() {
  const [user, setUser]               = useState(undefined); // undefined = loading
  const [profileData, setProfileData] = useState(null);
  const [profileExists, setProfileExists] = useState(null);
  const [profileError, setProfileError] = useState(false);

  // Keep the nav rail glued to the device's physical top edge as it rotates.
  useEffect(() => {
    const apply = () => document.body.setAttribute('data-rail', computeRail());
    apply();
    const so = window.screen && window.screen.orientation;
    if (so && so.addEventListener) so.addEventListener('change', apply);
    window.addEventListener('orientationchange', apply);
    window.addEventListener('resize', apply);
    return () => {
      if (so && so.removeEventListener) so.removeEventListener('change', apply);
      window.removeEventListener('orientationchange', apply);
      window.removeEventListener('resize', apply);
    };
  }, []);

  // Native app bridge: the Android shell loads the site with a one-time Firebase
  // custom token in the URL hash (#nativeToken=...). Consume it to sign the web
  // SDK in, then strip it from the URL so it can't be reused or bookmarked. No
  // effect for normal web visitors (no token present).
  useEffect(() => {
    const m = window.location.hash.match(/nativeToken=([^&]+)/);
    if (!m) return;
    const token = decodeURIComponent(m[1]);
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    signInWithCustomToken(auth, token).catch(e => console.error('Native session bridge failed:', e));
  }, []);

  // Auth listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser ?? null);
      setProfileError(false);
      if (!firebaseUser) {
        setProfileData(null); setProfileExists(null); return;
      }
      // Retry a couple times so a transient network blip doesn't strand the
      // user on an infinite spinner.
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const snap = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (snap.exists()) {
            setProfileData({ uid: firebaseUser.uid, ...snap.data() });
            setProfileExists(true);
            // One-time scrub: legacy accounts stored email on the world-readable
            // profile doc. Move it into the owner-only private doc and delete the
            // public copy. Fire-and-forget; runs once, then there's no email field.
            if (snap.data().email) {
              const uid = firebaseUser.uid;
              setDoc(doc(db, 'users', uid, 'private', 'data'), { email: snap.data().email }, { merge: true })
                .then(() => updateDoc(doc(db, 'users', uid), { email: deleteField() }))
                .catch(() => {});
            }
          } else {
            setProfileExists(false);
          }
          return;
        } catch (e) {
          if (attempt === 2) {
            console.error('Failed to load profile:', e);
            setProfileError(true);
          } else {
            await new Promise(r => setTimeout(r, 800 * (attempt + 1)));
          }
        }
      }
    });
    return unsub;
  }, []);

  // Real-time profile sync
  useEffect(() => {
    if (!user?.uid || profileExists !== true) return;
    const unsub = onSnapshot(doc(db, 'users', user.uid), snap => {
      if (snap.exists()) {
        setProfileData({ uid: user.uid, ...snap.data() });
      } else {
        // The account was deleted (self-serve or by an admin) while the session
        // was open. Don't keep showing a stale profile, sign out.
        try { window.YT8Native?.signOut?.(); } catch { /* not in the app */ }
        signOut(auth).catch(() => {});
      }
    });
    return unsub;
  }, [user?.uid, profileExists]);

  const handleProfileUpdate = (updates) =>
    setProfileData(prev => ({ ...prev, ...updates }));

  const claimPendingInvite = async () => {
    const token = localStorage.getItem(INVITE_TOKEN_KEY);
    if (!token) return;
    try {
      // Server validates the invite and upgrades the inviter's tile.
      const claim = httpsCallable(functions, 'claimInvite');
      await claim({ token });
    } catch (e) {
      console.error('Failed to claim invite:', e);
    } finally {
      localStorage.removeItem(INVITE_TOKEN_KEY);
    }
  };

  const handleSignOut = () => {
    setUser(null); setProfileData(null); setProfileExists(null);
  };

  const handleProfileCreated = async () => {
    if (!user) return;
    const snap = await getDoc(doc(db, 'users', user.uid));
    if (snap.exists()) {
      const newProfile = { uid: user.uid, ...snap.data() };
      setProfileData(newProfile);
      setProfileExists(true);
      await claimPendingInvite();
    }
  };

  if (user === undefined) return <FullPageSpinner />;
  // Block unverified email/password sessions from reaching the app at all.
  if (user && !user.emailVerified && (user.providerData || []).some(p => p.providerId === 'password')) {
    return <VerifyEmail user={user} onSignedOut={handleSignOut} />;
  }
  if (user && profileError) return <ProfileLoadError />;

  return (
    <Router>
      {SHOW_RAIL_DEBUG && <RailDebug />}
      <Routes>

        {/* ── Home ── */}
        <Route
          path="/"
          element={
            user && profileExists === false
              ? <Navigate to="/create-profile" replace />
              : <HomePage currentUser={user} currentUserProfile={profileData} onLogin={setUser} />
          }
        />

        {/* ── Create profile ── */}
        <Route
          path="/create-profile"
          element={
            !user ? <Navigate to="/" replace />
            : profileExists === true
              ? <Navigate to={introSeen(profileData) ? '/me' : '/onboarding'} replace />
            : <CreateProfile
                onProfileCreated={handleProfileCreated}
                onPhotoUploaded={url => handleProfileUpdate({ photoURL: url })}
              />
          }
        />

        {/* ── First-run intro carousel (once, right after account creation) ── */}
        <Route
          path="/onboarding"
          element={
            !user ? <Navigate to="/" replace />
            : profileExists === false ? <Navigate to="/create-profile" replace />
            : <OnboardingPage currentUser={user} currentUserProfile={profileData} />
          }
        />

        {/* ── /me redirects to your own profile (editing lives there now) ── */}
        <Route
          path="/me"
          element={
            !user ? <Navigate to="/" replace />
            : profileExists === null ? <FullPageSpinner />
            : profileExists === false ? <Navigate to="/create-profile" replace />
            : profileData?.username ? <Navigate to={`/${profileData.username}`} replace />
            : <FullPageSpinner />
          }
        />

        {/* ── Settings ── */}
        <Route
          path="/settings"
          element={
            !user ? <Navigate to="/" replace />
            : <Settings
                currentUser={user}
                currentUserProfile={profileData}
                onProfileUpdate={handleProfileUpdate}
                onSignOut={handleSignOut}
              />
          }
        />

        {/* ── Admin ── */}
        <Route path="/admin" element={<AdminPanel currentUser={user} currentUserProfile={profileData} />} />

        {/* ── People (search + connections) ── */}
        <Route
          path="/people"
          element={
            !user ? <Navigate to="/" replace />
            : <PeoplePage currentUser={user} currentUserProfile={profileData} />
          }
        />

        {/* ── Help / FAQ ── */}
        <Route path="/help" element={<Help currentUser={user} currentUserProfile={profileData} />} />
        <Route path="/feedback" element={<Feedback currentUser={user} currentUserProfile={profileData} />} />

        {/* ── Legal ── */}
        <Route path="/privacy" element={<PrivacyPolicy currentUser={user} currentUserProfile={profileData} />} />
        <Route path="/terms"   element={<TermsOfService currentUser={user} currentUserProfile={profileData} />} />
        <Route path="/legal"   element={<Legal currentUser={user} currentUserProfile={profileData} />} />
        <Route path="/delete-account" element={<DeleteAccount currentUser={user} currentUserProfile={profileData} />} />

        {/* ── Invite landing ── */}
        <Route
          path="/invite/:token"
          element={
            <InviteLanding
              currentUser={user}
              currentUserProfile={profileData}
            />
          }
        />

        {/* ── Public profile ── */}
        <Route
          path="/:username"
          element={
            <PublicProfile
              currentUser={user}
              currentUserProfile={profileData}
            />
          }
        />

      </Routes>
    </Router>
  );
}

export default App;
